import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "./portal/AuthContext";
import ProtectedRoute from "./portal/ProtectedRoute";
import PortalLayout from "./portal/PortalLayout";

import Login from "./portal/pages/Login";
import ResetPassword from "./portal/pages/ResetPassword";
import Cadastro from "./portal/pages/Cadastro";
import AguardandoAprovacao from "./portal/pages/AguardandoAprovacao";

import AssociateDashboard from "./portal/pages/AssociateDashboard";
import AssociateFiles from "./portal/pages/AssociateFiles";
import AssociateMeusDados from "./portal/pages/AssociateMeusDados";
import Forum from "./portal/pages/Forum";
import ForumTopic from "./portal/pages/ForumTopic";
import Reunioes from "./portal/pages/Reunioes";

import AdminDashboard from "./portal/pages/AdminDashboard";
import AdminAssociados from "./portal/pages/AdminAssociados";
import AdminAssociadoDetail from "./portal/pages/AdminAssociadoDetail";
import AdminAssociadoNovo from "./portal/pages/AdminAssociadoNovo";
import AdminAssociadosImportar from "./portal/pages/AdminAssociadosImportar";
import AdminMinhaAssinatura from "./portal/pages/AdminMinhaAssinatura";
import AdminFiles from "./portal/pages/AdminFiles";
import AdminBoletosLote from "./portal/pages/AdminBoletosLote";
import AdminEventos from "./portal/pages/AdminEventos";
import AdminConfiguracoes from "./portal/pages/AdminConfiguracoes";
import AdminUsuarios from "./portal/pages/AdminUsuarios";
import AdminNoticias from "./portal/pages/AdminNoticias";
import AdminTiposAssociados from "./portal/pages/AdminTiposAssociados";
import AdminRecibos from "./portal/pages/AdminRecibos";
import AdminRelatorios from "./portal/pages/AdminRelatorios";
import Estatuto from "./portal/pages/Estatuto";
import Onboarding from "./portal/pages/Onboarding";
import { ExpensesList, ExpenseDetail, AdminExpensesList } from "./portal/pages/Despesas";
import {
  ComercialLeadsList,
  ComercialNovoLead,
  ComercialLeadDetail,
  ComercialSegmentos,
} from "./portal/pages/Comercial";
import {
  ComercialKanban,
  ComercialEstagios,
} from "./portal/pages/ComercialKanban";
import { ComercialDashboard } from "./portal/pages/ComercialDashboard";
import {
  SignerList,
  SignerNew,
  SignerDetail,
  VerifyPage,
} from "./portal/pages/Assinador";

// Site institucional público
import HomePage from "./components/site/HomePage";
import GovernancePage from "./components/site/GovernancePage";
import NoticiasEArtigos from "./components/site/NoticiasEArtigos";

// Quando o app é servido pelo subdomínio do Portal do Associado
// (ex.: portal.ablemp.org.br) a raiz "/" deve levar direto ao Login,
// em vez do site institucional. Para todos os outros hosts, mantemos
// o comportamento padrão (site institucional público).
const IS_PORTAL_HOST =
  typeof window !== "undefined" &&
  /^portal\./i.test(window.location.hostname);

const RootRoute = () =>
  IS_PORTAL_HOST ? <Navigate to="/login" replace /> : <HomePage />;

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Site institucional público */}
            <Route path="/" element={<RootRoute />} />
            <Route path="/governanca" element={<GovernancePage />} />
            <Route path="/noticias-e-artigos" element={<NoticiasEArtigos />} />
            <Route path="/noticias-e-artigos/:slug" element={<NoticiasEArtigos />} />

            {/* Portal entry (público mas restrito por login) */}
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/cadastro" element={<Cadastro />} />
            <Route
              path="/aguardando-aprovacao"
              element={<AguardandoAprovacao />}
            />

            {/* Associate area */}
            <Route
              path="/portal/boas-vindas"
              element={
                <ProtectedRoute role="associado">
                  <Onboarding />
                </ProtectedRoute>
              }
            />
            <Route
              path="/portal"
              element={
                <ProtectedRoute roles={["associado", "conselheiro", "diretor"]}>
                  <PortalLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<AssociateDashboard />} />
              <Route path="meus-arquivos" element={<AssociateFiles />} />
              <Route path="meus-dados" element={<AssociateMeusDados />} />
              <Route path="forum" element={<Forum />} />
              <Route path="forum/:id" element={<ForumTopic />} />
              <Route path="reunioes" element={<Reunioes />} />
              <Route path="estatuto" element={<Estatuto />} />
              <Route path="despesas" element={<ExpensesList />} />
              <Route path="despesas/novo" element={<ExpenseDetail />} />
              <Route path="despesas/:id" element={<ExpenseDetail />} />
              <Route path="comercial/leads" element={<ComercialLeadsList />} />
              <Route path="comercial/leads/novo" element={<ComercialNovoLead />} />
              <Route path="comercial/leads/:id" element={<ComercialLeadDetail />} />
              <Route path="comercial/kanban" element={<ComercialKanban />} />
              <Route path="comercial/dashboard" element={<ComercialDashboard />} />
              <Route path="assinador" element={<SignerList />} />
              <Route path="assinador/novo" element={<SignerNew />} />
              <Route path="assinador/:id" element={<SignerDetail />} />
              <Route path="minha-assinatura" element={<AdminMinhaAssinatura />} />
            </Route>

            {/* Admin area */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute role="admin">
                  <PortalLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="associados" element={<AdminAssociados />} />
              <Route path="associados/novo" element={<AdminAssociadoNovo />} />
              <Route path="associados/importar" element={<AdminAssociadosImportar />} />
              <Route path="associados/:id" element={<AdminAssociadoDetail />} />
              <Route path="usuarios" element={<AdminUsuarios />} />
              <Route path="tipos-associados" element={<AdminTiposAssociados />} />
              <Route path="recibos" element={<AdminRecibos />} />
              <Route path="relatorios" element={<AdminRelatorios />} />
              <Route path="despesas" element={<AdminExpensesList />} />
              <Route path="despesas/novo" element={<ExpenseDetail />} />
              <Route path="despesas/:id" element={<ExpenseDetail />} />
              <Route path="comercial/leads" element={<ComercialLeadsList />} />
              <Route path="comercial/leads/novo" element={<ComercialNovoLead />} />
              <Route path="comercial/leads/:id" element={<ComercialLeadDetail />} />
              <Route path="comercial/segmentos" element={<ComercialSegmentos />} />
              <Route path="comercial/kanban" element={<ComercialKanban />} />
              <Route path="comercial/estagios" element={<ComercialEstagios />} />
              <Route path="comercial/dashboard" element={<ComercialDashboard />} />
              <Route path="minha-assinatura" element={<AdminMinhaAssinatura />} />
              <Route path="noticias" element={<AdminNoticias />} />
              <Route path="arquivos" element={<AdminFiles />} />
              <Route path="arquivos/boletos-lote" element={<AdminBoletosLote />} />
              <Route path="forum" element={<Forum />} />
              <Route path="forum/:id" element={<ForumTopic />} />
              <Route path="reunioes" element={<Reunioes />} />
              <Route path="eventos" element={<AdminEventos />} />
              <Route path="estatuto" element={<Estatuto />} />
              <Route path="configuracoes" element={<AdminConfiguracoes />} />
              <Route path="assinaturas" element={<SignerList />} />
              <Route path="assinaturas/novo" element={<SignerNew />} />
              <Route path="assinaturas/:id" element={<SignerDetail />} />
            </Route>

            {/* Public signature verification page */}
            <Route path="/verificar/:token" element={<VerifyPage />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="bottom-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
